import React, { useEffect, useState, useCallback } from "react";
import { X, Inbox, UserPlus, Archive, ExternalLink, Loader2 } from "lucide-react";
import { formsApi } from "../../lib/api";
import { toast } from "sonner";

export default function LeadsInbox({ form, open, onClose, onOpenCustomer }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!form?.id) return;
    setLoading(true);
    try { setLeads(await formsApi.submissions(form.id)); } catch (e) { /* */ } finally { setLoading(false); }
  }, [form?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open || !form) return null;

  const convert = async (s) => {
    try {
      const res = await formsApi.convertLead(s.id);
      toast.success("Lead added to customers");
      load();
      if (res.customerId && onOpenCustomer) onOpenCustomer(res.customerId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to convert."); }
  };
  const archive = async (s) => {
    try { await formsApi.archiveLead(s.id); load(); } catch (e) { toast.error("Failed to archive."); }
  };

  return (
    <div className="fixed inset-0 z-[115] flex justify-end" data-testid="leads-inbox">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-slate-50 h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-slate-900 truncate">{form.name} — Leads</h2>
            <p className="text-[12px] text-slate-500">{leads.length} submission{leads.length === 1 ? "" : "s"}</p>
          </div>
          <button onClick={onClose} data-testid="leads-inbox-close" className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4.5 w-4.5 text-slate-500" /></button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
          ) : leads.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 flex flex-col items-center text-center" data-testid="leads-empty">
              <Inbox className="h-8 w-8 text-slate-300 mb-2" />
              <div className="text-[13px] text-slate-500">No submissions yet. Share your form to start collecting leads.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="leads-list">
              {leads.map((s) => (
                <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-3.5" data-testid={`lead-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold text-slate-800">{s.name || "(no name)"}</div>
                      <div className="text-[12px] text-slate-500">{[s.email, s.phone].filter(Boolean).join(" · ")}</div>
                      {s.service && <div className="text-[12px] text-slate-500 mt-0.5">Service: {s.service}</div>}
                      {s.message && <div className="text-[12px] text-slate-500 mt-1 line-clamp-2">{s.message}</div>}
                    </div>
                    <span className={`shrink-0 inline-flex h-6 px-2 items-center rounded-full text-[10.5px] font-semibold ${s.status === "converted" ? "bg-emerald-100 text-emerald-700" : s.status === "archived" ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-700"}`}>{s.status}</span>
                  </div>
                  <div className="flex gap-2 mt-2.5">
                    {s.customerId ? (
                      <button onClick={() => onOpenCustomer && onOpenCustomer(s.customerId)} data-testid={`lead-open-${s.id}`} className="h-7 px-2.5 rounded-md text-[12px] font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> View customer</button>
                    ) : (
                      <button onClick={() => convert(s)} data-testid={`lead-convert-${s.id}`} className="h-7 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Add as customer</button>
                    )}
                    {s.status !== "archived" && (
                      <button onClick={() => archive(s)} data-testid={`lead-archive-${s.id}`} className="h-7 px-2.5 rounded-md text-[12px] font-medium text-slate-500 hover:bg-slate-100 flex items-center gap-1"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
